import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Keyboard from 'react-simple-keyboard';
import classNames from 'classnames';
import shortid from 'shortid';
import 'react-simple-keyboard/build/css/index.css';
import _ from 'lodash';

import { getKeyboardActive } from 'Common/UI/state/selectors';

import {
  CUSTOM_BUTTONS,
  CUSTOM_DISPLAY,
  DEFAULT_KEYBOARD_LAYOUT,
  LAYOUT_TYPES,
  NUMERIC_KEYBOARD_LAYOUT,
} from './constants';

const mapStateToProps = state => ({
  keyboardActive: getKeyboardActive(state),
});

const withKeyboard = InputComponent =>
  connect(mapStateToProps, null, null, { withRef: true })(
    class KeyboardInput extends React.Component {
      static propTypes = {
        onFocus: PropTypes.func,
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        onKeyboardClose: PropTypes.func,
        layoutType: PropTypes.oneOf([
          LAYOUT_TYPES.DEFAULT,
          LAYOUT_TYPES.NUMERIC,
        ]),
        display: PropTypes.object,
        mergeDisplay: PropTypes.bool,
        focusOnMount: PropTypes.bool,
        usePortal: PropTypes.bool,
        buttonTheme: PropTypes.arrayOf(PropTypes.object),
        appSidebar: PropTypes.bool,
        useMargin: PropTypes.bool,
        containerNode: PropTypes.object,
      };

      static defaultProps = {
        onFocus: () => {},
        onChange: () => {},
        onBlur: () => {},
        onTabPress: () => {},
        onKeyboardClose: () => {},
        layoutType: LAYOUT_TYPES.DEFAULT,
        display: CUSTOM_DISPLAY,

        // Combine the existing display fields with the custom ones
        mergeDisplay: true,

        focusOnMount: false,

        // Use this to render the keyboard outside of a modal
        usePortal: false,
        buttonTheme: [],
        appSidebar: false,
        useMargin: false,
        containerNode: null,
      };

      constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.keyboardRef = React.createRef();
        this.keyboardContainer = React.createRef();

        this.state = {
          show: false,
          shiftOn: false,
          capsOn: false,
        };

        this.instanceId = shortid.generate();
      }

      componentDidUpdate(prevProps, prevState) {
        const { value: prevValue } = prevProps;
        const { value } = this.props;

        if (prevValue !== value) {
          this.setKeyboardValue(value);
        }
      }

      componentDidMount() {
        const { value, focusOnMount } = this.props;
        // Initialize the keyboard's internal input state
        this.setKeyboardValue(value);

        if (focusOnMount) {
          if (this.inputRef.current) {
            this.inputRef.current.focus();
            this.setState({ show: true });
          }
        }

        document.addEventListener('mousedown', this.onBlur);
      }

      componentWillUnmount() {
        this.bodyNode.style[this.offsetProperty] = '';
        document.removeEventListener('mousedown', this.onBlur);
      }

      /**
       * Returns the offset style property to be used.
       */
      get offsetProperty() {
        const { useMargin } = this.props;

        return useMargin ? 'margin-bottom' : 'padding-bottom';
      }

      /**
       * Determines if the keyboard is covering the input.
       */
      get isKeyboardCoveringInput() {
        const { current: inputComponent } = this.inputRef;
        const { current: keyboardComponent } = this.keyboardRef;

        if (!inputComponent || !keyboardComponent) {
          return false;
        }

        const inputClientRect = inputComponent.inputRef.current.getBoundingClientRect();
        const keyboardClientRect = keyboardComponent.keyboard.keyboardDOM.getBoundingClientRect();

        const isOverlapping = !(
          inputClientRect.top > keyboardClientRect.bottom ||
          inputClientRect.right < keyboardClientRect.left ||
          inputClientRect.bottom < keyboardClientRect.top ||
          inputClientRect.left > keyboardClientRect.right
        );

        return isOverlapping;
      }

      /**
       * Returns the button theme used by the keyboard.
       */
      get buttonTheme() {
        const { buttonTheme: defaultButtonTheme, layoutType } = this.props;
        const { capsOn, shiftOn } = this.state;

        const buttonTheme = [...defaultButtonTheme];

        const buttonActive = {
          class: 'keyboard-btn-active',
        };
        let buttons = '';

        // Indicate caps button is active
        if (capsOn) {
          buttons += '{lock} ';
        }

        // Indicate shift button is active
        if (shiftOn) {
          buttons += '{shift} ';
        }

        if (layoutType === LAYOUT_TYPES.DEFAULT) {
          buttonTheme.push({
            class: 'keyboard-space-btn',
            buttons: '{space}',
          });
        }

        if (layoutType === LAYOUT_TYPES.NUMERIC) {
          buttonTheme.push({
            class: 'keyboard-numeric-btn',
            buttons: '1 2 3 4 5 6 7 8 9 0',
          });

          buttonTheme.push({
            class: 'keyboard-numeric-btn-action',
            buttons: `${CUSTOM_BUTTONS.CLOSE} ${CUSTOM_BUTTONS.CLEAR}`,
          });

          buttonTheme.push({
            class: 'keyboard-numeric-btn-enter',
            buttons: '{enter}',
          });

          buttonTheme.push({
            class: 'keyboard-numeric-btn-misc',
            buttons: ', .',
          });
        }

        if (buttons) {
          buttonActive.buttons = buttons;
          buttonTheme.push(buttonActive);
        }

        return buttonTheme;
      }

      /**
       * Gets the node used for applying padding or margin to in order to
       * adjust the screen for the keyboard's height.
       */
      get bodyNode() {
        const { containerNode } = this.props;

        return containerNode ? containerNode : document.body;
      }

      /**
       * Gets the node used to determine what needs to be scrolled.
       */
      get scrollContainerNode() {
        const { containerNode } = this.props;

        return containerNode ? containerNode : window;
      }

      /**
       * Adds spacing to the app div to adjust for the keyboard.
       */
      adjustScreenForKeyboard() {
        const { current: keyboardComponent } = this.keyboardRef;

        const keyboardClientRect = keyboardComponent.keyboard.keyboardDOM.getBoundingClientRect();
        const height = keyboardClientRect.height;

        this.bodyNode.style[this.offsetProperty] = `${height}px`;

        // Scroll by the height of the keyboard to keep the input and keyboard in view
        this.scrollContainerNode.scrollBy(0, height);
      }

      onFocus = e => {
        const {
          keyboardActive,
          onFocus,
          appSidebar,
          containerNode,
        } = this.props;
        const { show } = this.state;

        if (keyboardActive && !show) {
          if (appSidebar && !containerNode) {
            this.bodyNode.style['background-color'] = '#edf3f7';
          }

          // Manually reset the cursor position states for both the input element
          // and react-simple-keyboard to keep them in sync
          const keyboard = this.keyboardRef.current.keyboard;
          const inputNode = this.inputRef.current.inputRef.current;
          const currentValue = inputNode.value;
          inputNode.value = '';
          inputNode.value = currentValue;
          keyboard.caretPosition = currentValue.length;
          this.setKeyboardValue(currentValue);

          onFocus(e);
          this.setState({ show: true }, () => {
            if (this.isKeyboardCoveringInput) {
              this.adjustScreenForKeyboard();
            }
          });
        } else {
          onFocus(e);
        }
      };

      onBlur = e => {
        const { onBlur } = this.props;
        const { target } = e;
        const { show } = this.state;

        // Input isn't focused so just return
        if (!show) {
          return;
        }

        if (this.keyboardRef && this.inputRef.current) {
          if (this.inputRef.current.inputRef.current.contains(target)) {
            this.inputRef.current.focus();
          } else if (this.keyboardContainer.current.contains(target)) {
            // Do nothing for keyboard
          } else {
            this.inputRef.current.inputRef.current.blur();
            this.hideKeyboard();
            onBlur(e);
          }
        }
      };

      hideKeyboard() {
        const { onBlur, containerNode } = this.props;
        this.bodyNode.style[this.offsetProperty] = '';

        if (!containerNode) {
          this.bodyNode.style['background-color'] = '';
        }

        this.setState({ show: false });

        // Call the onBlur handler with the current value to allow
        // for cases where value is used in the onBlur i.e. formatting on blur
        onBlur({ value: this.inputRef.current.inputRef.current.value });
      }

      closeKeyboard = () => {
        const { onKeyboardClose, onBlur } = this.props;
        onBlur();
        this.hideKeyboard();
        onKeyboardClose();
      };

      onKeyPress = key => {
        const { onChange, onTabPress } = this.props;
        const { shiftOn, capsOn } = this.state;

        switch (key) {
          case CUSTOM_BUTTONS.CLEAR:
            onChange('');
            break;
          case CUSTOM_BUTTONS.TAB:
            this.hideKeyboard();
            onTabPress();
            break;
          case '{shift}':
            this.setState({ shiftOn: !shiftOn });
            break;
          case '{enter}':
          case CUSTOM_BUTTONS.CLOSE:
            this.closeKeyboard();
            break;
          case '{lock}':
            this.setState({ capsOn: !capsOn });
            break;
          default:
            this.setState({ shiftOn: false });
        }
      };

      setKeyboardValue = value => {
        const { current: keyboardComponent } = this.keyboardRef;

        if (keyboardComponent) {
          keyboardComponent.setInput(value);
        }
      };

      onInputChange = e => {
        const { onChange } = this.props;
        const {
          target: { value },
        } = e;
        onChange(e);
        this.setKeyboardValue(value);
      };

      onKeyboardChange = value => {
        const { onChange } = this.props;
        this.setKeyboardValue(value);
        onChange(value);
      };

      onInputBlur = e => {
        const { keyboardActive, onBlur } = this.props;
        const { show } = this.state;

        // Use default input blur behavior when keyboard isn't enabled
        if (!keyboardActive && !show) {
          onBlur(e);
        }
      };

      onInputKeydown = e => {
        const { keyboardActive } = this.props;

        if (!keyboardActive) {
          return;
        }

        const { keyCode } = e;
        const TAB_KEY_CODE = 9;

        if (keyCode === TAB_KEY_CODE) {
          this.hideKeyboard();
        }
      };

      focus() {
        if (this.inputRef.current) {
          this.inputRef.current.focus();
        }
      }

      render() {
        const {
          dispatch,
          keyboardActive,
          onTabPress,
          onFocus,
          onBlur,
          onChange,
          display,
          maxLength,
          mergeDisplay,
          focusOnMount,
          usePortal,
          onKeyboardClose,
          buttonTheme,
          appSidebar,
          layoutType,
          innerRef,
          useMargin,
          containerNode,
          ...inputProps
        } = this.props;
        const { show, capsOn, shiftOn } = this.state;
        const keyboardWrapperClass = classNames('keyboard--wrapper', {
          'keyboard-hidden': !show || !keyboardActive,
          'keyboard-sidebar': appSidebar,
        });
        const baseClassName = `react-simple-keyboard_${this.instanceId}`;
        const keyboardClass = classNames('hg-theme-default', {
          keyboard: layoutType === LAYOUT_TYPES.DEFAULT,
          'keyboard-numeric': layoutType === LAYOUT_TYPES.NUMERIC,
        });

        const wrapperStyles = {};
        let layoutName = 'default';

        if (capsOn && !shiftOn) {
          layoutName = 'shift';
        } else if (capsOn && shiftOn) {
          layoutName = 'default';
        } else if (shiftOn) {
          layoutName = 'shift';
        }

        const layout =
          layoutType === LAYOUT_TYPES.NUMERIC
            ? NUMERIC_KEYBOARD_LAYOUT
            : DEFAULT_KEYBOARD_LAYOUT;

        const keyboard = (
          <div
            className={keyboardWrapperClass}
            style={wrapperStyles}
            ref={this.keyboardContainer}
          >
            <Keyboard
              ref={this.keyboardRef}
              onChange={this.onKeyboardChange}
              onKeyPress={this.onKeyPress}
              layout={layout}
              display={display}
              mergeDisplay={mergeDisplay}
              maxLength={maxLength}
              baseClass={baseClassName}
              buttonTheme={this.buttonTheme}
              layoutName={layoutName}
              theme={keyboardClass}
            />
          </div>
        );

        return (
          <React.Fragment>
            <InputComponent
              onFocus={this.onFocus}
              onChange={this.onInputChange}
              onBlur={this.onInputBlur}
              onKeyDown={this.onInputKeydown}
              {...inputProps}
              ref={this.inputRef}
              maxLength={maxLength}
            />
            {usePortal
              ? ReactDOM.createPortal(keyboard, this.bodyNode)
              : keyboard}
          </React.Fragment>
        );
      }
    }
  );

export default withKeyboard;

/**
 * Pulls the value from the keyboard or input.
 * @param {Event|String} e
 * @returns String
 */
export const getValue = e => {
  return e.target ? e.target.value : e;
};

/**
 * Gets the KeyboardInput component ref.
 * @param {Object} keyboardInputRef
 */
export const getInputComponent = keyboardInputRef => {
  return _.get(
    keyboardInputRef,
    ['current', 'wrappedInstance', 'inputRef', 'current'],
    null
  );
};

export const focusInput = keyboardInputRef => {
  const keyboardInput = getInputComponent(keyboardInputRef);

  if (keyboardInput) {
    keyboardInput.focus();
  }
};



// WEBPACK FOOTER //
// ./src/common/Components/Keyboard/withKeyboard.js